import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";

const BEACON_POLL_MS = 30000;
const BEACON_TEXT = {
  fr: { here: "Le groupe est ici", at: "signalé à" },
  en: { here: "The group is here", at: "reported at" },
};

// Interactive map of the Fêtes 2027 locations — OpenStreetMap tiles, no API key.
const MAIN_SITES = [
  {
    pos: [48.6689, -1.4431],
    name: { fr: "Prieuré saint Léonard — Vains", en: "Priory of Saint Léonard — Vains" },
    desc: {
      fr: "Cœur des Fêtes : messes, vêpres des Anges, veillée internationale, procession des reliques, symposium et soirée de Gala.",
      en: "Heart of the Feasts: masses, Vespers of the Angels, international vigil, procession of the relics, symposium and Gala evening.",
    },
  },
  {
    pos: [48.6361, -1.5115],
    name: { fr: "Mont-Saint-Michel", en: "Mont-Saint-Michel" },
    desc: {
      fr: "Traversée des grèves, lavement des pieds, Requiem pour un Conquérant à l'Abbatiale, descente aux flambeaux.",
      en: "Crossing of the strands, washing of the feet, Requiem for a Conqueror at the Abbey, torchlight descent.",
    },
  },
  {
    pos: [48.6845, -1.3628],
    name: { fr: "Avranches — Notre-Dame-des-Champs", en: "Avranches — Notre-Dame-des-Champs" },
    desc: {
      fr: "Messe d'ouverture, ouverture officielle au Jardin des Plantes, conférence et vernissage de l'exposition « Saint Léonard ».",
      en: "Opening mass, official opening at the Jardin des Plantes, lecture and preview of the “Saint Léonard” exhibition.",
    },
  },
  {
    pos: [48.656, -1.439],
    name: { fr: "Bergerie des Grèves", en: "Bergerie des Grèves" },
    desc: {
      fr: "Méchoui du samedi midi chez François Cerbonney, avant la traversée vers le Mont.",
      en: "Saturday méchoui at François Cerbonney's, before the crossing to the Mont.",
    },
  },
];

const VILLAGES = [
  { pos: [48.6606, -1.4472], name: "Vains" },
  { pos: [48.6853, -1.4794], name: "Genêts" },
  { pos: [48.7028, -1.4409], name: "Bacilly" },
  { pos: [48.717, -1.459], name: "Dragey-Ronthon" },
  { pos: [48.731, -1.516], name: "Saint-Jean-le-Thomas" },
  { pos: [48.753, -1.454], name: "Sartilly" },
  { pos: [48.697, -1.393], name: "Marcey-les-Grèves" },
  { pos: [48.665, -1.298], name: "Tirepied" },
  { pos: [48.723, -1.166], name: "Brécey" },
  { pos: [48.838, -1.221], name: "Villedieu-les-Poêles" },
  { pos: [48.351, -1.199], name: "Fougères" },
];

const VILLAGE_DESC = {
  fr: "Village d'accueil — hospitalité, remise des livrets & des écus, veillée locale du 1ᵉʳ juillet.",
  en: "Host village — hospitality, booklets & escus, local vigil on 1 July.",
};

const CROSSING_ROUTE = [
  { pos: [48.656, -1.439], time: "14:00", label: { fr: "Départ — Bergerie des Grèves", en: "Departure — Bergerie des Grèves" } },
  { pos: [48.6495, -1.456], time: "14:45", label: { fr: "Passage des grèves de Vains", en: "Crossing the Vains strands" } },
  { pos: [48.6425, -1.472], time: "15:30", label: { fr: "Traversée de la Sée et de la Sélune", en: "Fording the Sée and Sélune rivers" } },
  { pos: [48.638, -1.493], time: "16:30", label: { fr: "Dernière ligne droite face au Mont", en: "Final stretch facing the Mont" } },
  { pos: [48.6361, -1.5115], time: "17:30", label: { fr: "Arrivée au Mont — lavement des pieds", en: "Arrival at the Mont — washing of the feet" } },
];

const CROSSING_TITLE = {
  fr: "Traversée des grèves · sam. 3 juillet",
  en: "Crossing of the strands · Sat 3 July",
};

const diamond = (color, size, border) =>
  L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2 - 4],
    html: `<div style="width:${size}px;height:${size}px;background:${color};border:2px solid ${border};transform:rotate(45deg);box-shadow:0 2px 6px rgba(20,15,10,0.45);"></div>`,
  });

// Map a free-text event location (fr) to coordinates on the bay map.
export function matchFetesLocation(loc) {
  const s = (loc || "").toLowerCase();
  if (!s) return null;
  if (s.includes("bergerie") || s.includes("cerbonney")) return { pos: [48.656, -1.439], zoom: 13 };
  if (s.includes("baie du mont")) return { pos: [48.653, -1.49], zoom: 12 };
  if (s.includes("abbatiale") || s.includes("mont-saint-michel") || s.includes("mont saint michel")) return { pos: [48.6361, -1.5115], zoom: 15 };
  if (s.includes("avranches") || s.includes("notre-dame") || s.includes("jardin des plantes") || s.includes("providence")) return { pos: [48.6845, -1.3628], zoom: 14 };
  if (s.includes("prieuré") || s.includes("parc du prieur")) return { pos: [48.6689, -1.4431], zoom: 14 };
  const villageKeys = [["genêts", 1], ["bacilly", 2], ["dragey", 3], ["saint-jean", 4], ["sartilly", 5], ["marcey", 6], ["tirepied", 7], ["brécey", 8], ["villedieu", 9], ["fougères", 10]];
  for (const [key, idx] of villageKeys) {
    if (s.includes(key)) return { pos: VILLAGES[idx].pos, zoom: 12 };
  }
  if (s.includes("vains")) return { pos: [48.6689, -1.4431], zoom: 14 };
  if (s.includes("village")) return { pos: [48.7, -1.4], zoom: 10 };
  return null;
}

export default function FetesMap({ locale = "fr", focus = null, showBeacon = true }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const beaconRef = useRef(null);
  const lang = locale === "en" ? "en" : "fr";

  useEffect(() => {
    if (!ref.current || mapRef.current) return undefined;
    const map = L.map(ref.current, { scrollWheelZoom: false });
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);

    const mainIcon = diamond("#7b1f2b", 18, "#d4af37");
    const villageIcon = diamond("#d4af37", 12, "#fff");

    const bayBounds = [];
    MAIN_SITES.forEach((s) => {
      bayBounds.push(s.pos);
      L.marker(s.pos, { icon: mainIcon })
        .addTo(map)
        .bindPopup(
          `<strong style="font-family:serif;font-size:14px;color:#7b1f2b;">${s.name[lang]}</strong><br/><span style="font-size:12px;">${s.desc[lang]}</span>`
        );
    });
    VILLAGES.forEach((v) => {
      if (v.name !== "Fougères") bayBounds.push(v.pos);
      L.marker(v.pos, { icon: villageIcon })
        .addTo(map)
        .bindPopup(
          `<strong style="font-family:serif;font-size:14px;color:#8a6d1f;">${v.name}</strong><br/><span style="font-size:12px;">${VILLAGE_DESC[lang]}</span>`
        );
    });

    // Crossing route — dashed line + timed waypoints (Saturday 3 July, 14:00 → 17:30).
    L.polyline(CROSSING_ROUTE.map((w) => w.pos), {
      color: "#7b1f2b",
      weight: 3,
      dashArray: "7 9",
      opacity: 0.85,
    }).addTo(map);
    const crossingMarkers = [];
    CROSSING_ROUTE.forEach((w) => {
      const m = L.circleMarker(w.pos, {
        radius: 7,
        color: "#fff",
        weight: 2,
        fillColor: "#d4af37",
        fillOpacity: 1,
      })
        .addTo(map)
        .bindTooltip(w.time, {
          permanent: true,
          direction: "top",
          offset: [0, -8],
          className: "crossing-time-tooltip",
        })
        .bindPopup(
          `<strong style="font-family:serif;font-size:13px;color:#7b1f2b;">${CROSSING_TITLE[lang]}</strong><br/>` +
          `<span style="font-size:12px;"><strong>${w.time}</strong> — ${w.label[lang]}</span>`
        );
      crossingMarkers.push(m);
    });
    const toggleCrossingTooltips = () => {
      const show = map.getZoom() >= 11;
      crossingMarkers.forEach((m) => (show ? m.openTooltip() : m.closeTooltip()));
    };
    map.on("zoomend", toggleCrossingTooltips);
    map.whenReady(toggleCrossingTooltips);

    map.fitBounds(L.latLngBounds(bayBounds).pad(0.08));
    map.on("click", () => map.scrollWheelZoom.enable());

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [lang]);

  // Fly to a focused event location and open a labelled popup.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus || !focus.pos) return;
    map.flyTo(focus.pos, focus.zoom || 13, { duration: 0.9 });
    L.popup({ offset: [0, -6] })
      .setLatLng(focus.pos)
      .setContent(
        `<strong style="font-family:serif;font-size:14px;color:#7b1f2b;">${focus.title || ""}</strong>${focus.subtitle ? `<br/><span style="font-size:12px;">${focus.subtitle}</span>` : ""}`
      )
      .openOn(map);
  }, [focus]);

  // Live group beacon — poll the public endpoint and keep a pulsing marker in sync.
  useEffect(() => {
    if (!showBeacon) return undefined;
    let cancelled = false;
    const bt = BEACON_TEXT[lang];
    const syncBeacon = async () => {
      let data = null;
      try {
        data = (await api.get("/presence/beacon")).data;
      } catch {
        return;
      }
      const map = mapRef.current;
      if (cancelled || !map) return;
      if (!data || !data.active) {
        if (beaconRef.current) {
          beaconRef.current.remove();
          beaconRef.current = null;
        }
        return;
      }
      const pos = [data.lat, data.lng];
      const time = data.updated_at
        ? new Date(data.updated_at).toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-GB", { hour: "2-digit", minute: "2-digit" })
        : "";
      const html =
        `<strong style="font-family:serif;font-size:14px;color:#7b1f2b;">◉ ${bt.here}</strong>` +
        `${data.label ? `<br/><span style="font-size:12px;">${data.label}</span>` : ""}` +
        `${time ? `<br/><span style="font-size:11px;color:#8a6d1f;">${bt.at} ${time}</span>` : ""}`;
      try {
        if (!beaconRef.current) {
          const icon = L.divIcon({
            className: "",
            iconSize: [22, 22],
            iconAnchor: [11, 11],
            popupAnchor: [0, -14],
            html: '<div class="beacon-pulse"><div class="beacon-dot"></div></div>',
          });
          beaconRef.current = L.marker(pos, { icon, zIndexOffset: 1000 }).addTo(map).bindPopup(html);
        } else {
          beaconRef.current.setLatLng(pos).setPopupContent(html);
        }
      } catch {
        beaconRef.current = null;
      }
    };
    syncBeacon();
    const id = setInterval(syncBeacon, BEACON_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      beaconRef.current = null;
    };
  }, [lang, showBeacon]);

  return (
    <div
      ref={ref}
      data-testid="fetes-map"
      style={{
        height: "480px",
        width: "100%",
        border: "1px solid var(--sand)",
        boxShadow: "0 3px 14px rgba(20,15,10,0.12)",
        zIndex: 0,
      }}
    />
  );
}
